import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { getPostBySlug, getSortedPosts } from "@/data/blogPosts";

/** Tiny markdown-lite renderer: ## H2, - bullets, [text](url) links, blank-line paragraphs. */
const renderContent = (content: string) => {
  const blocks = content.split(/\n\n+/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith("## ")) {
      return (
        <h2
          key={i}
          className="text-2xl font-semibold text-primary mt-10 mb-4"
        >
          {renderInline(trimmed.slice(3))}
        </h2>
      );
    }
    if (trimmed.split("\n").every((l) => l.trim().startsWith("- "))) {
      const items = trimmed.split("\n").map((l) => l.replace(/^-\s+/, ""));
      return (
        <ul key={i} className="list-disc pl-6 my-4 space-y-2 text-primary/85">
          {items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="my-4 leading-relaxed text-primary/85">
        {renderInline(trimmed)}
      </p>
    );
  });
};

/** Inline: **bold** and [text](url). */
const renderInline = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1]) {
      parts.push(<strong key={key++}>{match[1]}</strong>);
    } else if (match[2] && match[3]) {
      const href = match[3];
      const isInternal = href.startsWith("/");
      parts.push(
        isInternal ? (
          <Link
            key={key++}
            to={href}
            className="text-secondary hover:underline font-medium"
          >
            {match[2]}
          </Link>
        ) : (
          <a
            key={key++}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-secondary hover:underline font-medium"
          >
            {match[2]}
          </a>
        )
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  const canonical = `https://functionalfoodie.co.za/blog/${post.slug}`;
  const related = getSortedPosts()
    .filter((p) => p.slug !== post.slug && p.category === post.category)
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords.join(", ")} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content="Dalya Hockman" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.metaDescription,
          datePublished: post.date,
          dateModified: post.date,
          author: {
            "@type": "Person",
            name: "Dalya Hockman",
            jobTitle: "Registered Dietitian",
            affiliation: {
              "@type": "Organization",
              name: "Functional Foodie Registered Dietitians",
            },
          },
          publisher: {
            "@type": "Organization",
            name: "Functional Foodie Registered Dietitians",
            url: "https://functionalfoodie.co.za",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": canonical,
          },
          keywords: post.keywords.join(", "),
          articleSection: post.category,
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://functionalfoodie.co.za/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://functionalfoodie.co.za/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: post.title,
              item: canonical,
            },
          ],
        })}</script>
      </Helmet>

      <article className="pt-52 md:pt-40 pb-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <nav className="text-sm text-primary/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-secondary">Home</Link>
            {" / "}
            <Link to="/blog" className="hover:text-secondary">Blog</Link>
            {" / "}
            <span className="text-primary/80">{post.category}</span>
          </nav>

          <div className="text-xs uppercase tracking-wide text-secondary font-semibold mb-3">
            {post.category}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 text-sm text-primary/60 mb-8 pb-8 border-b border-border">
            <span>By Dalya Hockman, RD(SA)</span>
            <span aria-hidden>•</span>
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("en-ZA", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          </div>

          <div className="prose-content">{renderContent(post.content)}</div>

          <div className="mt-12 p-6 bg-accent/30 rounded-lg border border-border">
            <h3 className="text-lg font-semibold text-primary mb-2">
              Work with a registered dietitian in Johannesburg
            </h3>
            <p className="text-primary/80 mb-4 text-sm">
              Dalya Hockman is a Registered Dietitian with the HPCSA and a
              member of the Association for Dietetics in South Africa (ADSA).
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/booking"
                className="inline-block bg-secondary text-white px-5 py-2 rounded-md font-medium hover:opacity-90 transition-opacity"
              >
                Book a consultation
              </Link>
              <Link
                to="/contact"
                className="inline-block border border-primary text-primary px-5 py-2 rounded-md font-medium hover:bg-primary hover:text-white transition-colors"
              >
                Contact us
              </Link>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <h3 className="text-xl font-semibold text-primary mb-6">
                Related articles
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="block p-4 bg-white border border-border rounded-md hover:shadow-md transition-shadow"
                  >
                    <div className="text-xs text-secondary font-semibold mb-1">
                      {r.category}
                    </div>
                    <div className="text-sm font-medium text-primary leading-snug">
                      {r.title}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
};

export default BlogPost;